import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Book, Users, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

interface SidebarSection {
  id: string;
  title: string;
  items: { id: string; title: string }[];
}

interface GuideSidebarProps {
  sections: SidebarSection[];
  activeSection?: string;
  onSectionClick?: (sectionId: string) => void;
  guideType: "producer" | "crew";
}

const GuideSidebar = ({
  sections,
  activeSection,
  onSectionClick,
  guideType,
}: GuideSidebarProps) => {
  const [expandedSections, setExpandedSections] = useState<string[]>(sections.map((s) => s.id));

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId],
    );
  };

  const handleItemClick = (sectionId: string, itemId: string) => {
    const targetId = `${sectionId}-${itemId}`;
    onSectionClick?.(targetId);

    // Scroll to section
    setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <aside className="sticky top-[73px] hidden h-[calc(100vh-73px)] w-64 shrink-0 overflow-y-auto border-r border-border bg-card/50 lg:block">
      <div className="p-4">
        <div className="mb-4">
          <Link
            to="/field-guide"
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <Book className="h-4 w-4" />← Back to Field Guide
          </Link>
        </div>

        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
          {guideType === "producer" ? (
            <>
              <Users className="h-5 w-5 text-primary" />
              Producer Guide
            </>
          ) : (
            <>
              <Zap className="h-5 w-5 text-secondary" />
              Crew Guide
            </>
          )}
        </h2>

        <nav className="space-y-1">
          {sections.map((section) => (
            <div key={section.id}>
              <button
                onClick={() => toggleSection(section.id)}
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
              >
                <span>{section.title}</span>
                {expandedSections.includes(section.id) ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {expandedSections.includes(section.id) && (
                <div className="ml-3 mt-1 space-y-1 border-l border-border pl-3">
                  {section.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(section.id, item.id)}
                      className={cn(
                        "w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors",
                        activeSection === `${section.id}-${item.id}`
                          ? "bg-primary/10 font-medium text-primary"
                          : "text-muted-foreground hover:bg-muted/30 hover:text-foreground",
                      )}
                    >
                      {item.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
};

export default GuideSidebar;
